import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, googleProvider } from '../lib/firebase';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { BACKEND_URL } from '../config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {

    // Set a timeout to ensure loading doesn't get stuck
    const loadingTimeout = setTimeout(() => {
      console.log('Auth loading timeout - setting loading to false');
      setLoading(false);
    }, 10000); // 10 second timeout

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(loadingTimeout); // Clear timeout when auth state changes
      
      console.log('🔄 onAuthStateChanged triggered, user:', firebaseUser ? firebaseUser.email : 'null');
      
      if (firebaseUser) {
        // User is signed in - get their ID token and send to backend
        try {
          console.log('📝 Getting Firebase ID token...');
          const idToken = await firebaseUser.getIdToken();
          console.log('✅ Got Firebase ID token, length:', idToken.length);
          
          // Call backend to create/update user profile
          const backendUrl = BACKEND_URL;
          let apiUrl = backendUrl;
          if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
            apiUrl = '';
          }
          
          console.log('🌐 Calling backend auth endpoint:', `${apiUrl}/api/auth/firebase-login`);
          
          // Add timeout to fetch request
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
          
          try {
            const response = await fetch(`${apiUrl}/api/auth/firebase-login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              credentials: 'include',
              signal: controller.signal
            });

            clearTimeout(timeoutId);
            console.log('📥 Backend auth response status:', response.status);
            
            if (response.ok) {
              const userData = await response.json();
              console.log('🔍 FULL BACKEND RESPONSE:', JSON.stringify(userData, null, 2));
              console.log('🔍 User data being set:', {
                email: userData.user?.email,
                role: userData.user?.role,
                is_admin: userData.user?.is_admin,
                created_at: userData.user?.created_at,
                roles: userData.user?.roles
              });
              setUser(userData.user);
              setIsAuthenticated(true);
              console.log('✅ Backend authentication successful!');
              console.log('✅ User authenticated:', userData.user.email);
              console.log('✅ User role:', userData.user.role);
            } else {
              const errorText = await response.text();
              console.error('Backend authentication failed:', response.status, errorText);
              // Still set user from Firebase even if backend fails
              setUser({
                email: firebaseUser.email,
                name: firebaseUser.displayName,
                firebase_uid: firebaseUser.uid,
                role: 'USER'
              });
              setIsAuthenticated(true);
            }
          } catch (fetchError) {
            clearTimeout(timeoutId);
            if (fetchError.name === 'AbortError') {
              console.error('Backend auth request timeout');
            } else {
              console.error('Backend auth fetch error:', fetchError);
            }
            // Still set user from Firebase even if backend fails
            setUser({
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              firebase_uid: firebaseUser.uid,
              role: 'USER'
            });
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error('Firebase token error:', error);
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        // User is signed out
        console.log('🚪 User signed out');
        setUser(null);
        setIsAuthenticated(false);
      }
      
      console.log('✅ Auth state processing complete, setting loading to false');
      setLoading(false);
    });

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, []);

  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      console.log('🚀 Starting Google login with popup...');
      
      const result = await signInWithPopup(auth, googleProvider);
      console.log('✅ Google popup login successful:', result.user.email);
      
      // The onAuthStateChanged listener will handle the rest
      
    } catch (error) {
      console.error('❌ Google login error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      setLoading(false);
      
      // Handle specific Firebase errors
      if (error.code === 'auth/popup-closed-by-user') {
        alert('Login cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        alert('Popup was blocked by your browser. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/unauthorized-domain') {
        alert('Authentication Error: This domain is not authorized in Firebase Console.');
      } else {
        alert(`Login failed: ${error.message}`);
      }
    }
  };

  const logout = async () => {
    try {
      // Call backend logout first
      const backendUrl = BACKEND_URL;
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }
      
      await fetch(`${apiUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      }).catch(console.error); // Don't fail logout if backend call fails
      
      // Sign out from Firebase
      await signOut(auth);
      
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getIdToken = async () => {
    try {
      // For test admin user, return a mock token
      if (user && user.email === 'test@admin.com') {
        return 'mock-admin-token';
      }
      
      if (auth.currentUser) {
        return await auth.currentUser.getIdToken();
      }
      return null;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  const refreshUserProfile = async () => {
    try {
      if (!auth.currentUser) return null;
      
      const idToken = await getIdToken();
      if (!idToken) return null;
      
      // Call backend to get updated user profile
      const backendUrl = BACKEND_URL;
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }
      
      const response = await fetch(`${apiUrl}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include'
      });
      
      if (response.ok) {
        const updatedUserData = await response.json();
        setUser(updatedUserData.user || updatedUserData); // Update user state
        console.log('User profile refreshed:', updatedUserData);
        return updatedUserData;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      return null;
    }
  };

  // Test login function for development/testing
  const testLogin = async () => {
    try {
      setLoading(true);
      
      // Create a mock user for testing
      const mockUser = {
        id: 'test-user-123',
        firebase_uid: 'test-firebase-uid',
        email: 'test@admin.com',
        name: 'Test Admin',
        is_designer: true,
        designer_status: 'approved',
        role: 'SUPER_ADMIN'
      };
      
      setUser(mockUser);
      setIsAuthenticated(true);
      setLoading(false);
      
      console.log('Test admin login successful');
    } catch (error) {
      console.error('Test login error:', error);
      setLoading(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    loginWithGoogle,
    logout,
    getIdToken,
    refreshUserProfile,
    testLogin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};